import React, { useEffect, useState } from 'react';
import { useAuth } from './../contexts/AuthContext';
import { useTheme } from './../contexts/ThemeContext';
import { Calendar, AlertCircle } from 'lucide-react';

interface Milestone {
  _id: string;
  title: string;
  description: string;
  dueDate: string;
  createdAt: string;
}

const getApiBaseUrl = (): string => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    const protocol = window.location.protocol;

    // Special handling for WebContainer preview URLs
    if (hostname.includes('webcontainer.io') || hostname.includes('webcontainer-api.io')) {
      const baseUrl = hostname.replace('--5173--', '--5002--');
      return `${protocol}//${baseUrl}/api`;
    }

    // Local development
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return `${protocol}//${hostname}:5002/api`;
    }

    // Render or other deployment domains
    return `${protocol}//${hostname}/api`;
  }

  // Fallback to env or default
  return import.meta.env.VITE_API_URL || 'http://localhost:5002/api';
};



const MilestoneList: React.FC<{ projectId: string }> = ({ projectId }) => {
  const { user } = useAuth();
  const { theme } = useTheme();
  const [milestones, setMilestones] = useState<Milestone[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const API_BASE_URL = getApiBaseUrl();

  useEffect(() => {
    fetchMilestones();
  }, [projectId]);

  const fetchMilestones = async () => {
    try {
      const token = localStorage.getItem('accessToken');
      const response = await fetch(`${API_BASE_URL}/milestones/project/${projectId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Failed to fetch milestones');
      const data = await response.json();
      setMilestones(data.milestones || []);
    } catch (err) {
      setError('Unable to load milestones');
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="text-sm text-gray-500">Loading milestones...</div>;

  return (
    <div className="space-y-4">
      <h3 className="text-lg font-semibold mb-2">Project Milestones</h3>

      {error && (
        <div className="flex items-center text-red-500 text-sm bg-red-50 border border-red-200 rounded p-2">
          <AlertCircle className="w-4 h-4 mr-2" />
          {error}
        </div>
      )}

      {milestones.length === 0 ? (
        <p className="text-sm text-gray-500">No milestones yet for this project.</p>
      ) : (
        <ul className="space-y-2">
          {milestones.map((milestone) => (
            <li
              key={milestone._id}
              className={`border ${theme === 'dark' ? 'border-gray-700 bg-gray-900 text-gray-200' : 'border-gray-200 bg-white text-gray-800'} p-4 rounded shadow-sm`}
            >
              <div className="font-medium text-base">{milestone.title}</div>
              <div className="text-sm text-gray-500 mb-1">{milestone.description}</div>
              <div className="flex items-center text-sm text-blue-500">
                <Calendar className="w-4 h-4 mr-1" />
                Due: {new Date(milestone.dueDate).toLocaleDateString()}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default MilestoneList;
